import {
  Box,
  FormControlLabel,
  FormHelperText,
  OutlinedInput,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useState, useRef, useEffect, useMemo } from "react";
import "./FormIban.scss";
import theme from "../../../muiTheme";
import EnvirormentContent from "../../EnvirormentContent/EnvirormentContent";
import { ManagementUpdateCup } from "../../../core/models/cupsCifSelector/cupsCifSelector.model";
import {
  getBase64,
  getIbanStateData,
  validateIban,
} from "../../../utils/components/managements/managements.util";
import { useTranslation } from "react-i18next";
import {
  CupsIbanValidator,
  DomiciliationBillingInfo,
  DomiciliationFileArray,
  UpdatePaymentMethodRequestBody,
} from "../../../core/models/managementsUpdate/managementUpdateDomiciliation/managementUpdateDomiciliation.model";
import ManagementUploadFile from "../../Managements/ManagementsUploadFile/ManagementsUploadFile";
import { fetchBipValidation } from "../../../core/services/managements/managements.service";
import { getBipValidationRequestBody } from "../../../core/models/managements/managements.model";
import useOcrValidation from "../../../utils/components/managements/ocrValidation/useOcrValidation";

interface FormIbanProps {
  cup: ManagementUpdateCup;
  setIsValidIban?: (valid: boolean) => void;
  billingInfo?: DomiciliationBillingInfo;
  isAgent?: boolean | null;
  setIsFileLoaded?: (loaded: boolean) => void;
  setIbanValidations?: React.Dispatch<React.SetStateAction<CupsIbanValidator>>;
  setRequestBody?: React.Dispatch<
    React.SetStateAction<UpdatePaymentMethodRequestBody[]>
  >;
  setFileArray: React.Dispatch<React.SetStateAction<DomiciliationFileArray[]>>;
  fileArray: DomiciliationFileArray[];
  onIbanSelectionChange?: (
    cupsCode: string,
    isChangedFromActive: boolean,
  ) => void;
  onOcrRequestStart?: () => void;
  onOcrRequestEnd?: () => void;
}

const NEW_IBAN_RADIO_VALUE = "__NEW_IBAN__";

export const IbanLabel = ({
  isNewIban = false,
  isActive = false,
}: {
  isNewIban?: boolean;
  isActive?: boolean;
}) => {
  const { color, backgroundColor, icon, label } = getIbanStateData(
    isNewIban,
    isActive,
  );

  const { t } = useTranslation();

  return (
    <Typography
      variant="body2"
      component="span"
      width={{ xs: "80px", lg: "100px" }}
      sx={{
        backgroundColor,
        color,
        textWrap: "nowrap",
        marginRight: "0",
      }}
      className="state-chip"
      height={"fit-content"}
      alignSelf={{ xs: "start", md: "center" }}
      marginRight={"20px"}
      order={{ xs: "-1", md: "0" }}
    >
      <span aria-hidden="true" className={icon} />
      {t(label)}
    </Typography>
  );
};

export const IbanInput = ({
  iban,
  isDisabled,
  setNewIban,
  setSelectedIban,
  isActive,
  inputIndex,
  error,
  setError,
  existingIbans,
  onBlur,
}: {
  iban?: string;
  isDisabled?: boolean;
  setNewIban?: React.Dispatch<React.SetStateAction<string>>;
  setSelectedIban?: React.Dispatch<React.SetStateAction<string>>;
  isActive?: boolean;
  inputIndex?: number;
  error?: "ibanFormat" | "duplicatedIban" | null;
  setError?: React.Dispatch<
    React.SetStateAction<"ibanFormat" | "duplicatedIban" | null>
  >;
  existingIbans?: string[];
  onBlur?: (iban: string) => void;
}) => {
  const isDesktopViewport = useMediaQuery("(min-width: 1200px)");
  const isMobileViewport = useMediaQuery("(max-width: 766px)");
  const isNewIbanInput = !isDisabled;

  const { t } = useTranslation();

  const ibanInputBoxes = [
    {
      order: 1,
      width: "10%",
      type: "text",
      maxLength: 2,
    },
    {
      order: 2,
      width: "10%",
      type: "text",
      maxLength: 2,
    },
    {
      order: 3,
      width: "16%",
      type: "text",
      maxLength: 4,
    },
    {
      order: 4,
      width: "16%",
      type: "text",
      maxLength: 4,
    },
    {
      order: 5,
      width: "10%",
      type: "text",
      maxLength: 2,
    },
    {
      order: 6,
      width: "35%",
      type: "text",
      maxLength: 14,
    },
  ];

  const getIbanFragments = () => {
    let startIndex = 0;
    return ibanInputBoxes.map((input) => {
      const fragment =
        iban?.slice(startIndex, startIndex + input.maxLength) || "";
      startIndex += input.maxLength;
      return fragment;
    });
  };

  const splitIbanToFragments = (ibanString: string) => {
    let startIndex = 0;
    return ibanInputBoxes.map((input) => {
      const fragment =
        ibanString.slice(startIndex, startIndex + input.maxLength) || "";
      startIndex += input.maxLength;
      return fragment;
    });
  };

  const joinFragmentsToIban = (fragments: string[]) => fragments.join("");

  const [newIbanValue, setNewIbanValue] =
    useState<string[]>(getIbanFragments());

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleInputChange = (
    index: number,
    value: string,
    maxLength: number,
  ) => {
    const trimmedValue = value.slice(0, maxLength);

    const updatedFragments = [...newIbanValue];
    updatedFragments[index] = trimmedValue;
    setNewIbanValue(updatedFragments);

    if (setNewIban && isNewIbanInput) {
      const fullIban = joinFragmentsToIban(updatedFragments);
      setNewIban(fullIban);

      if (setSelectedIban) {
        setSelectedIban(NEW_IBAN_RADIO_VALUE);
      }
    }

    // Move focus to next input
    if (
      trimmedValue.length === maxLength &&
      index < ibanInputBoxes.length - 1
    ) {
      const nextInput = inputRefs.current[index + 1];
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const ibanFragments = isDisabled ? getIbanFragments() : newIbanValue;

  const ibanInputIndex =
    inputIndex !== undefined ? `IBAN ${inputIndex + 1}` : "IBAN";

  // mobile: show single input with spaces
  const getSpacedIban = (value: string) => {
    let startIndex = 0;
    return ibanInputBoxes
      .map((input) => {
        const fragment =
          value.slice(startIndex, startIndex + input.maxLength) || "";
        startIndex += input.maxLength;
        return fragment;
      })
      .filter(Boolean)
      .join(" ");
  };

  const handleMobileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s+/g, "").toUpperCase();
    const trimmedValue = value.slice(0, 28);
    const fragments = splitIbanToFragments(trimmedValue);
    setNewIbanValue(fragments);

    if (isNewIbanInput) {
      setNewIban && setNewIban(trimmedValue);
      setSelectedIban && setSelectedIban(NEW_IBAN_RADIO_VALUE);
    }
  };

  return (
    <>
      <Stack
        flexDirection={"row"}
        justifyContent={"space-between"}
        gap={"20px"}
        className="iban-container"
      >
        {isDesktopViewport && (
          <Typography
            sx={{ textWrap: "nowrap" }}
            fontWeight={600}
            color={theme.palette.grey[500]}
            marginRight={isDisabled ? "41px" : "0"}
          >
            {isNewIbanInput ? t("Gestiones.Nuevo IBAN") : ibanInputIndex}
          </Typography>
        )}
        <Stack width={{ xs: "100%", lg: "65%" }} maxWidth={"670px"}>
          <Stack
            width={"100%"}
            flexDirection={"row"}
            gap={"6px"}
            onFocus={() => {
              if (!!error) {
                if (!!setError) setError(null);
              }
            }}
            onBlur={(e) => {
              if (isNewIbanInput) {
                // Check if focus is still inside stack
                const isStillInsideStack = e.currentTarget.contains(
                  e.relatedTarget as Node,
                );

                if (!isStillInsideStack) {
                  const fullIban = newIbanValue.join("");
                  if (fullIban.length > 0) {
                    // validate format
                    if (!validateIban(fullIban)) {
                      if (!!setError) setError("ibanFormat");
                    }
                    // validate duplicates
                    else if (
                      existingIbans &&
                      existingIbans.includes(fullIban)
                    ) {
                      if (!!setError) setError("duplicatedIban");
                    }
                  }
                  onBlur?.(fullIban);
                }
              }
            }}
          >
            {isDesktopViewport &&
              ibanInputBoxes.map((input, index) => (
                <OutlinedInput
                  key={input.order}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                  onChange={(e) =>
                    handleInputChange(
                      index,
                      e.target.value.toUpperCase(),
                      input.maxLength,
                    )
                  }
                  name={"iban-number"}
                  type={input.type}
                  value={(ibanFragments[index] || "").toUpperCase()}
                  disabled={isDisabled}
                  sx={{
                    width: input.width,

                    letterSpacing: "1px",
                    fontWeight: 500,
                    "& input": {
                      padding: { xs: "12px 8px", lg: "12px 10px" },
                    },
                  }}
                  inputProps={{
                    maxLength: input.maxLength,
                  }}
                />
              ))}
            {!isDesktopViewport && (
              <Stack width={"100%"}>
                <Stack
                  flexDirection={"row"}
                  alignItems={"end"}
                  justifyContent={"space-between"}
                >
                  <Typography
                    paddingLeft={"6px"}
                    paddingBottom={"10px"}
                    fontWeight={600}
                    color={theme.palette.grey[500]}
                    sx={{
                      textWrap: "nowrap",
                    }}
                    marginRight={isDisabled ? "41px" : "0"}
                  >
                    {isNewIbanInput
                      ? t("Gestiones.Nuevo IBAN")
                      : ibanInputIndex}
                  </Typography>
                  <Box paddingRight={"6px"} paddingBottom={"10px"}>
                    <IbanLabel isActive={isActive} isNewIban={isNewIbanInput} />
                  </Box>
                </Stack>
                <OutlinedInput
                  onChange={handleMobileInputChange}
                  name={"iban-number"}
                  type={"text"}
                  value={getSpacedIban(
                    joinFragmentsToIban(newIbanValue),
                  ).toUpperCase()}
                  disabled={isDisabled}
                  sx={{
                    letterSpacing: "1px",
                    fontWeight: 500,
                    width: "100%",
                  }}
                  inputProps={{
                    maxLength: 33,
                  }}
                />
              </Stack>
            )}
          </Stack>
          <Box paddingRight="6px">
            {error === "ibanFormat" && isNewIbanInput && (
              <FormHelperText
                error
                sx={{
                  marginLeft: 0,
                  marginTop: "8px",
                  textAlign: "right",
                }}
              >
                {t("Gestiones.Formato de IBAN incorrecto")}
              </FormHelperText>
            )}
            {error === "duplicatedIban" && isNewIbanInput && (
              <FormHelperText
                error
                sx={{
                  marginLeft: 0,
                  marginTop: "8px",
                  textAlign: "right",
                }}
              >
                {t("Gestiones.IBAN duplicado")}
              </FormHelperText>
            )}
          </Box>
        </Stack>

        <Stack
          justifyContent={isDesktopViewport ? "start" : "end"}
          marginBottom={!!error ? "20px" : "0"}
        >
          <FormControlLabel
            sx={{ marginRight: "0" }}
            value={isNewIbanInput ? NEW_IBAN_RADIO_VALUE : iban}
            label={
              !isMobileViewport &&
              isDesktopViewport && (
                <IbanLabel isNewIban={isNewIbanInput} isActive={isActive} />
              )
            }
            control={<Radio disableRipple />}
          />
        </Stack>
      </Stack>
    </>
  );
};

const FormIban = ({
  cup,
  billingInfo,
  isAgent,
  setIbanValidations,
  setRequestBody,
  setFileArray,
  fileArray,
  onIbanSelectionChange,
  onOcrRequestStart,
  onOcrRequestEnd,
}: FormIbanProps) => {
  const { supplyAddress, envirorment, cupsCode } = cup;

  const { t } = useTranslation();
  const { formatOcrErrorMessageText } = useOcrValidation();

  const ibanList = useMemo(
    () =>
      billingInfo?.Cobros.filter((item) => item.EsTipoCobroDomiciliado)
        .sort(
          (a, b) =>
            (b.EsActivo === true ? 1 : 0) - (a.EsActivo === true ? 1 : 0),
        )
        .sort((a, b) => (b.EsActivo ? 1 : -1))
        .filter(
          (item, index, self) =>
            self.findIndex((i) => i.Iban === item.Iban) === index,
        ),
    [billingInfo?.Cobros],
  );

  const currentActiveIban = ibanList?.find((item) => item.EsActivo);

  const [selectedIban, setSelectedIban] = useState<string>(
    currentActiveIban?.Iban ?? "",
  );
  const [newIban, setNewIban] = useState<string>("");
  const [error, setError] = useState<"ibanFormat" | "duplicatedIban" | null>(
    null,
  );
  const [isOcrLoading, setIsOcrLoading] = useState<boolean>(false);
  const lastOcrIbanRef = useRef<string | null>(null);
  const latestOcrRequestIdRef = useRef(0);

  useEffect(() => {
    if (!selectedIban) return; // no radio selected yet, nothing to sync

    if (lastOcrIbanRef.current && lastOcrIbanRef.current !== newIban) {
      lastOcrIbanRef.current = null;
      latestOcrRequestIdRef.current += 1;
    }

    if (selectedIban !== currentActiveIban?.Iban) {
      const updateRequestBody = (ibanValue: string) => {
        setRequestBody?.((prev: UpdatePaymentMethodRequestBody[]) => {
          const existingIndex = prev.findIndex(
            (item) => item.cups === cupsCode,
          );
          if (existingIndex !== -1) {
            const updated = [...prev];
            updated[existingIndex] = {
              cups: cupsCode,
              iban: ibanValue,
              comentariosInternos: "",
            };
            return updated;
          } else {
            return [
              ...prev,
              { cups: cupsCode, iban: ibanValue, comentariosInternos: "" },
            ];
          }
        });
      };

      if (selectedIban === NEW_IBAN_RADIO_VALUE) {
        updateRequestBody(newIban);

        // ADD: update validations on every IBAN change
        const isValid =
          validateIban(newIban) &&
          !ibanList?.map((item) => item.Iban).includes(newIban);

        setIbanValidations?.((prev) => ({
          ...prev,
          [cup.cupsCode]: {
            validIban: isValid,
            validDocument: isAgent
              ? (prev[cup.cupsCode]?.validDocument ?? false)
              : true,
          },
        }));
      } else {
        updateRequestBody(selectedIban);
      }
    } else {
      setRequestBody?.((prev: UpdatePaymentMethodRequestBody[]) =>
        prev.filter((item) => item.cups !== cupsCode),
      );
    }

    onIbanSelectionChange?.(cupsCode, selectedIban !== currentActiveIban?.Iban);
  }, [
    newIban,
    selectedIban,
    cupsCode,
    currentActiveIban?.Iban,
    cup.cupsCode,
    ibanList,
    isAgent,
    onIbanSelectionChange,
    setIbanValidations,
    setRequestBody,
  ]);

  const updateFileArray = (file: File | null, cups: string) => {
    if (file) {
      setFileArray((prev) => [
        ...prev.filter((item) => item.cups !== cups),
        { cups, file },
      ]);
    } else {
      setFileArray((prev) => prev.filter((item) => item.cups !== cups));
    }
  };

  const handleRemoveFile = () => {
    lastOcrIbanRef.current = null;
    latestOcrRequestIdRef.current += 1;
    updateFileArray(null, cupsCode);
    setRequestBody?.((prev) =>
      prev.map((item) =>
        item.cups === cupsCode && item.iban === newIban
          ? { ...item, comentariosInternos: "" }
          : item,
      ),
    );
    // only for new iban input
    if (selectedIban === NEW_IBAN_RADIO_VALUE) {
      setIbanValidations?.((prev) => ({
        ...prev,
        [cup.cupsCode]: {
          validIban: prev[cup.cupsCode]?.validIban ?? false,
          validDocument: false,
        },
      }));
    }
  };

  const handleIbanChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    updateFileArray(null, cupsCode);
    const selectedValue = event.target.value;
    setSelectedIban(selectedValue);
    lastOcrIbanRef.current = null;
    latestOcrRequestIdRef.current += 1;

    if (selectedValue !== NEW_IBAN_RADIO_VALUE) {
      // validIban andn validDocument true when selecting existing iban
      setIbanValidations?.((prev) => ({
        ...prev,
        [cup.cupsCode]: { validIban: true, validDocument: true },
      }));
      updateFileArray(null, cupsCode);
    } else {
      // validate new iban
      const isValid =
        validateIban(newIban) &&
        !ibanList?.map((item) => item.Iban).includes(newIban);

      setIbanValidations?.((prev) => ({
        ...prev,
        [cup.cupsCode]: {
          validIban: isValid,
          validDocument: isAgent ? false : true,
        },
      }));
    }
  };

  const validateDocumentOcr = async (
    file: File,
    iban: string,
  ): Promise<string> => {
    const requestId = ++latestOcrRequestIdRef.current;
    lastOcrIbanRef.current = iban;
    setIsOcrLoading(true);
    onOcrRequestStart?.();
    setRequestBody?.((prev) =>
      prev.map((item) =>
        item.cups === cupsCode && item.iban === iban
          ? { ...item, comentariosInternos: "" }
          : item,
      ),
    );

    let validationMessage = "El servicio de OCR ha fallado.";
    try {
      const base64file = await getBase64(file).then(
        (res) => (res as string).split("base64,")[1],
      );
      const requestBody: getBipValidationRequestBody = {
        document_type: "cambio_domiciliacion",
        pdf_base64: base64file ?? "",
        reference_data: {
          iban,
          razon_social: cup.customerDescription ?? "",
        },
      };

      const validationResponse = await fetchBipValidation(requestBody);
      const responseDetail = (
        validationResponse as unknown as { detail?: string }
      ).detail;

      if (responseDetail?.includes("INVALID_DOCUMENT_TYPE")) {
        validationMessage = "El documento adjunto no es válido";
      } else if (
        validationResponse.validation_result.message === "Validation ko"
      ) {
        const hasValidationResults =
          !!validationResponse.validation_result.result;
        const ocrErrorMessages = hasValidationResults
          ? formatOcrErrorMessageText(validationResponse)
          : "";
        validationMessage = ocrErrorMessages
          ? `Los siguientes campos del documento adjunto presentan errores de validación: ${ocrErrorMessages}`
          : "El documento adjunto no ha superado la validación OCR";
      } else if (
        validationResponse.validation_result.message === "Validation ok"
      ) {
        validationMessage =
          "El documento adjunto ha sido validado correctamente";
      }
    } catch (error: unknown) {
      const errorDetail =
        typeof error === "object" &&
        error !== null &&
        "detail" in error &&
        typeof error.detail === "string"
          ? error.detail
          : "";
      if (errorDetail.includes("INVALID_DOCUMENT_TYPE")) {
        validationMessage = "El documento adjunto no es válido";
      }
    } finally {
      setIsOcrLoading(false);
      onOcrRequestEnd?.();
    }

    if (requestId === latestOcrRequestIdRef.current) {
      setRequestBody?.((prev) =>
        prev.map((item) =>
          item.cups === cupsCode && item.iban === iban
            ? { ...item, comentariosInternos: validationMessage }
            : item,
        ),
      );
    }

    return validationMessage;
  };

  if (!billingInfo) return null;

  return (
    <Stack className="form-iban">
      <Stack className="form-iban__cif-info" flexDirection={"row"}>
        <span className="t-icon-map brand-red form-iban__cif-info--icon"></span>
        <Stack flexDirection={"column"}>
          {/* cups info */}
          <Typography component="p" variant="body2">
            {supplyAddress}
          </Typography>
          <Typography
            component="p"
            variant="body2"
            sx={{ color: theme.palette.grey[500] }}
          >
            <EnvirormentContent
              service={envirorment}
              variant={"body2"}
              showServiceText={false}
            />{" "}
            {`${cupsCode}`}
          </Typography>
        </Stack>
      </Stack>
      {/* available iban codes */}
      <Box padding={"14px 0"}>
        <RadioGroup value={selectedIban} onChange={handleIbanChange}>
          {ibanList?.map((item, i) => (
            <IbanInput
              inputIndex={i}
              isActive={item.EsActivo}
              key={item.Iban}
              iban={item.Iban}
              isDisabled
            />
          ))}
          {/*new iban input */}
          <IbanInput
            onBlur={(iban) => {
              const isValid =
                validateIban(iban) &&
                !ibanList?.map((item) => item.Iban).includes(iban);

              setIbanValidations?.((prev) => ({
                ...prev,
                [cup.cupsCode]: {
                  validIban: isValid,
                  validDocument: prev[cup.cupsCode]?.validDocument ?? false,
                },
              }));

              const mandate = fileArray.find((item) => item.cups === cupsCode);
              if (
                selectedIban === NEW_IBAN_RADIO_VALUE &&
                isValid &&
                mandate &&
                lastOcrIbanRef.current !== iban
              ) {
                validateDocumentOcr(mandate.file, iban);
              }
            }}
            setSelectedIban={setSelectedIban}
            iban={newIban}
            setNewIban={setNewIban}
            error={error}
            setError={setError}
            existingIbans={ibanList?.map((item) => item.Iban)}
          />
          {isAgent && selectedIban === NEW_IBAN_RADIO_VALUE && (
            <Stack
              className="management-update-domiciliation__upload-file-container"
              spacing={4}
            >
              <Stack component="div" spacing={2}>
                <Typography variant="h3" component="p">
                  {t("Gestiones.Mandato SEPA")}
                </Typography>
                <Typography variant="body2" component="p">
                  {t("Gestiones.Adjuntar mandato SEPA")}
                </Typography>
              </Stack>
              <Stack component="div" spacing={2}>
                <ManagementUploadFile
                  title={t("Gestiones.Mandato SEPA")}
                  isRequired
                  downloadDocumentType="01"
                  isLoading={isOcrLoading}
                  downloadButtonText={t("Gestiones.Descargar mandato SEPA")}
                  onFileUpload={(id, file) => {
                    updateFileArray(file, cupsCode);
                    setIbanValidations?.((prev) => ({
                      ...prev,
                      [cup.cupsCode]: {
                        validIban: prev[cup.cupsCode]?.validIban ?? false,
                        validDocument: true,
                      },
                    }));
                    validateDocumentOcr(file, newIban);
                  }}
                  onFileRemove={handleRemoveFile}
                />
              </Stack>
            </Stack>
          )}
        </RadioGroup>
      </Box>
    </Stack>
  );
};

export default FormIban;
